import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit, Trash2, CreditCard, AlertCircle, Calendar, DollarSign, RefreshCw, Search, Globe, Layers, Clock, ArrowUp, ArrowDown, Download, X, PowerOff, Wallet, Mail, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { useTranslation } from '../lib/i18n';
import { getApiBaseUrl } from '../lib/apiClient';
import { companyPaymentsAPI, companiesAPI, domainsAPI, settingsAPI, hostingPricesAPI, domainPricesAPI, accountsAPI, accountTransactionsAPI } from '../lib/api';

// Modal Component
const PaymentModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Center the modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Modal header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Modal body */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const CompanyPayments = () => {
  const { t } = useTranslation();
  const [companyPayments, setCompanyPayments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyDomains, setCompanyDomains] = useState([]);
  const [hostingPackages, setHostingPackages] = useState([]); // Hosting fiyat tanımlamaları
  const [hostingPriceAutoFilled, setHostingPriceAutoFilled] = useState(false); // Fiyat otomatik dolduruldu mu?
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'all', 'active', 'inactive'
  const [packageTypeFilter, setPackageTypeFilter] = useState(''); // '', 'domain', 'hosting', 'ssl', 'email', 'reklam', 'danismanlik', 'other'
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendingPayment, setExtendingPayment] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [extendFormData, setExtendFormData] = useState({
    addToAccounting: false,
    accountId: '',
    amount: '',
    description: ''
  });
  const [loadingWhois, setLoadingWhois] = useState(false);
  const [loadingDomainPrice, setLoadingDomainPrice] = useState(false);
  const [loadingHostingPrice, setLoadingHostingPrice] = useState(false);
  const [groupByCompany, setGroupByCompany] = useState(false);
  const [sortColumn, setSortColumn] = useState('next_payment_date');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [exchangeRates, setExchangeRates] = useState({
    USD: 1,
    EUR: 1,
    GBP: 1,
    GOLD: 1
  });
  const [formData, setFormData] = useState({
    company_id: '',
    package_name: '',
    package_type: 'domain',
    domain_id: '',
    purchase_price: '',
    sale_price: '',
    purchase_currency: 'TRY',
    sale_currency: 'TRY',
    frequency: 'yearly',
    start_date: '',
    next_payment_date: '',
    payment_probability: 100,
    notes: ''
  });

  useEffect(() => {
    fetchData();
    fetchExchangeRates();
    fetchHostingPackages();
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sayfa başlığını güncelle
  useEffect(() => {
    document.title = 'Siparişler - Domain Control';
  }, []);

  const fetchAccounts = async () => {
    try {
      const data = await accountsAPI.getAll();
      setAccounts(data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const settings = await settingsAPI.getAll();
      setExchangeRates({
        USD: parseFloat(settings.exchange_rate_usd) || 1,
        EUR: parseFloat(settings.exchange_rate_eur) || 1,
        GBP: parseFloat(settings.exchange_rate_gbp) || 1,
        GOLD: parseFloat(settings.exchange_rate_gold) || 1
      });
    } catch (error) {
      console.error('Exchange rate fetch error:', error);
    }
  };

  // Para birimi sembolü getir
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'TRY': '₺',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'GOLD': 'Altın'
    };
    return symbols[currency] || currency;
  };

  // Kar hesaplama fonksiyonu (TL cinsinden)
  const calculateProfit = (purchasePrice, purchaseCurrency, salePrice, saleCurrency) => {
    let purchaseInTRY = parseFloat(purchasePrice) || 0;
    let saleInTRY = parseFloat(salePrice) || 0;

    // Alış fiyatını TL'ye çevir
    const purchaseCurr = purchaseCurrency || 'TRY';
    if (purchaseCurr !== 'TRY') {
      const rate = exchangeRates[purchaseCurr] || 1;
      purchaseInTRY = purchaseInTRY * rate;
    }

    // Satış fiyatını TL'ye çevir
    const saleCurr = saleCurrency || 'TRY';
    if (saleCurr !== 'TRY') {
      const rate = exchangeRates[saleCurr] || 1;
      saleInTRY = saleInTRY * rate;
    }

    return saleInTRY - purchaseInTRY;
  };

  const fetchHostingPackages = async () => {
    try {
      // Hosting fiyat tanımlamalarından paketleri getir (Supabase'den)
      const hostingPrices = await hostingPricesAPI.getAll();
      // Sadece aktif olanları al ve hosting_name'leri listele
      const activeHostings = (Array.isArray(hostingPrices) ? hostingPrices : [])
        .filter(hp => hp.is_active)
        .map(hp => ({
          id: hp.id,
          name: hp.hosting_name,
          hosting_price_id: hp.id,
          purchase_price: hp.purchase_price,
          sale_price: hp.sale_price,
          purchase_currency: hp.purchase_currency,
          sale_currency: hp.sale_currency
        }));
      setHostingPackages(activeHostings);
    } catch (error) {
      console.error('Hosting packages fetch error:', error);
      toast.error(t('companyPayments.hostingPackagesError', { error: error.message || t('common.unknownError') }));
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [paymentsRes, companiesRes] = await Promise.all([
        companyPaymentsAPI.getAll(),
        companiesAPI.getAll()
      ]);
      setCompanyPayments(Array.isArray(paymentsRes) ? paymentsRes : []);
      setCompanies(Array.isArray(companiesRes) ? companiesRes : []);
    } catch (error) {
      console.error('Data fetch error:', error);
      toast.error(t('companyPayments.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    
    
    // Fiyat alanları için değeri doğrudan kullan (number input'tan gelen değer string olabilir)
    const newFormData = {
      ...formData,
      [name]: value
    };
    
    
    setFormData(newFormData);

    // Firma değiştiğinde domainleri getir (her zaman yükle, paket tipi kontrolü dropdown gösteriminde yapılacak)
    if (name === 'company_id') {
      if (value) {
        try {
          const domains = await domainsAPI.getAll();
          // UUID string karşılaştırması (parseInt kullanma!)
          const companyDomains = domains.filter(d => String(d.company_id) === String(value));
          setCompanyDomains(Array.isArray(companyDomains) ? companyDomains : []);
        } catch (error) {
          console.error('Domain fetch error:', error);
          toast.error(t('companyPayments.domainsLoadError', { error: error.response?.data?.error || error.message }));
          setCompanyDomains([]);
        }
      } else {
        setCompanyDomains([]);
        setFormData(prev => ({ ...prev, domain_id: '' }));
      }
    }

    // Paket tipi domain olarak değiştiğinde domainleri getir
    if (name === 'package_type') {
      if (value === 'domain' && newFormData.company_id) {
        try {
          const domains = await domainsAPI.getAll();
          // UUID string karşılaştırması (parseInt kullanma!)
          const companyDomains = domains.filter(d => String(d.company_id) === String(newFormData.company_id));
          setCompanyDomains(Array.isArray(companyDomains) ? companyDomains : []);
        } catch (error) {
          console.error('Domain fetch error:', error);
          toast.error(t('companyPayments.domainsLoadError', { error: error.response?.data?.error || error.message }));
          setCompanyDomains([]);
        }
      } else {
        setCompanyDomains([]);
        setFormData(prev => ({ ...prev, domain_id: '' }));
      }
    }

    // Hosting paketi seçildiğinde package_name'i otomatik doldur ve fiyatı otomatik getir
    if (name === 'selected_hosting_id' && value) {
      
      // UUID string karşılaştırması (parseInt kullanma!)
      const selectedHosting = hostingPackages.find(h => String(h.id) === String(value));
      if (selectedHosting) {
        // Hosting adını package_name olarak ayarla
        setFormData(prev => ({
          ...prev,
          package_name: selectedHosting.name
        }));
        
        // Hosting seçildiğinde otomatik olarak fiyatları getir
        // Sadece düzenleme modunda ve aynı hosting seçiliyse ve fiyatlar zaten doldurulmuşsa koru
        const isSameHosting = editingPayment && formData.selected_hosting_id && formData.selected_hosting_id === value;
        const hasValidPrices = (selectedHosting.purchase_price !== null && selectedHosting.purchase_price !== undefined) && 
                               (selectedHosting.sale_price !== null && selectedHosting.sale_price !== undefined);
        
        // Eğer fiyatlar varsa ve (aynı hosting değilse veya fiyatlar daha önce doldurulmamışsa), fiyatları getir
        if (hasValidPrices && (!isSameHosting || !hostingPriceAutoFilled)) {
          setFormData(prev => ({
            ...prev,
            purchase_price: String(selectedHosting.purchase_price) || '',
            sale_price: String(selectedHosting.sale_price) || '',
            purchase_currency: selectedHosting.purchase_currency || 'TRY',
            sale_currency: selectedHosting.sale_currency || 'TRY'
          }));
          setHostingPriceAutoFilled(true);
          toast.success(t('companyPayments.hostingPriceSuccessAuto'), { duration: 3000 });
        } else if (isSameHosting && hostingPriceAutoFilled) {
        } else if (!hasValidPrices) {
        }
      }
    } else if (name === 'selected_hosting_id' && !value) {
      // Hosting seçimi kaldırıldığında (Manuel giriş yap seçildiğinde)
      setHostingPriceAutoFilled(false);
      
      // Eğer package_name bir hosting paketine eşleşiyorsa, package_name'i temizle (hosting paketinden ayrıl)
      if (newFormData.package_name && hostingPackages.length > 0) {
        const matchingHosting = hostingPackages.find(h => h.name === newFormData.package_name);
        if (matchingHosting) {
          // package_name hosting paketine eşleşiyorsa, temizle (kullanıcı manuel giriş yapacak)
          setFormData(prev => ({
            ...prev,
            package_name: ''
          }));
        }
      }
    }
    
    // ÖNEMLİ: purchase_price değiştiğinde sale_price'ı etkileme
    // purchase_price 0 girilse bile sale_price değeri korunmalı
    
    // Fiyat alanları manuel değiştirildiğinde flag'i sıfırla (kullanıcı manuel giriş yapıyor)
    if (name === 'purchase_price' || name === 'sale_price' || name === 'purchase_currency' || name === 'sale_currency') {
      setHostingPriceAutoFilled(false);
    }

    // Sıklığa göre otomatik next_payment_date hesapla
    if (name === 'start_date' || name === 'frequency') {
      calculateNextPaymentDate(name === 'start_date' ? value : newFormData.start_date, name === 'frequency' ? value : newFormData.frequency);
    }
  };

  const calculateNextPaymentDate = (startDate, frequency) => {
    if (!startDate || !frequency) return;
    
    const start = new Date(startDate);
    let nextDate = new Date(start);
    
    if (frequency === 'monthly') {
      nextDate.setMonth(nextDate.getMonth() + 1);
    } else if (frequency === 'yearly') {
      nextDate.setFullYear(nextDate.getFullYear() + 1);
    } else if (frequency === 'one-time') {
      nextDate = null;
    }
    
    if (nextDate) {
      setFormData(prev => ({
        ...prev,
        next_payment_date: nextDate.toISOString().split('T')[0]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Fiyat değerlerini number'a çevir (boş string kontrolü ile)
      // ÖNEMLİ: purchase_price 0 olsa bile sale_price değeri korunmalı
      const purchasePrice = formData.purchase_price === '' || formData.purchase_price === null || formData.purchase_price === undefined 
        ? 0 
        : parseFloat(formData.purchase_price);
      const salePrice = formData.sale_price === '' || formData.sale_price === null || formData.sale_price === undefined 
        ? 0 
        : parseFloat(formData.sale_price);
      
      
      // purchase_price 0 olsa bile sale_price değeri korunmalı
      // selected_hosting_id veritabanında bir kolon değil, sadece frontend'de kullanılıyor
      const { selected_hosting_id, ...formDataWithoutHostingId } = formData;
      
      // UUID alanlar için boş string'leri ve geçersiz değerleri null'a çevir
      const cleanUUIDField = (value) => {
        if (value === '' || value === null || value === undefined || value === '0' || value === 0) {
          return null;
        }
        const strValue = String(value).trim();
        // Boş string veya sadece "0" ise null döndür
        if (!strValue || strValue === '0') {
          return null;
        }
        // Sadece rakamlardan oluşan kısa değerler (muhtemelen eski INTEGER ID) null'a çevir
        // UUID'ler genellikle tire içerir veya daha uzundur
        if (strValue.length < 8 && /^[0-9]+$/.test(strValue)) {
          return null;
        }
        return strValue;
      };
      
      const submitData = {
        ...formDataWithoutHostingId,
        // UUID alanlar - boş string ise null yap
        company_id: cleanUUIDField(formData.company_id),
        domain_id: formData.domain_id ? cleanUUIDField(formData.domain_id) : null,
        // Fiyat alanları
        purchase_price: isNaN(purchasePrice) ? 0 : purchasePrice,
        sale_price: isNaN(salePrice) ? 0 : salePrice, // purchase_price'dan bağımsız olarak sale_price korunmalı
        // amount alanı (veritabanında NOT NULL, sale_price değerini kullan)
        amount: isNaN(salePrice) ? 0 : salePrice,
        // currency alanı (varsa formData'dan, yoksa sale_currency'den)
        currency: formData.currency || formData.sale_currency || 'TRY',
        payment_probability: parseFloat(formData.payment_probability) || 100
      };
      
      
      if (editingPayment) {
        await companyPaymentsAPI.update(editingPayment.id, submitData);
        toast.success(t('companyPayments.paymentUpdated'));
      } else {
        await companyPaymentsAPI.create(submitData);
        toast.success(t('companyPayments.paymentAdded'));
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Payment operation error:', error);
      console.error('Error response:', error.response);
      if (error.response?.data?.errors) {
        // Validation hatalarını göster
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        toast.error(errorMessages);
      } else {
        toast.error(error.response?.data?.error || (editingPayment ? t('companyPayments.updateError', 'Tekrarlı ödeme güncellenemedi') : t('companyPayments.addError', 'Tekrarlı ödeme eklenemedi')));
      }
    }
  };

  const handleEdit = async (payment) => {
    setEditingPayment(payment);
    
    // Fiyat değerlerini string'e çevir (0 değerini de koru)
    const purchasePrice = payment.purchase_price !== null && payment.purchase_price !== undefined ? String(payment.purchase_price) : '';
    const salePrice = payment.sale_price !== null && payment.sale_price !== undefined ? String(payment.sale_price) : '';
    
    
    // Eğer package_type hosting ise, package_name'e göre hosting'i bul
    let selectedHostingId = '';
    let wasHostingPreviouslySelected = false;
    if (payment.package_type === 'hosting' && payment.package_name && hostingPackages.length > 0) {
      const matchingHosting = hostingPackages.find(h => h.name === payment.package_name);
      if (matchingHosting) {
        selectedHostingId = String(matchingHosting.id);
        wasHostingPreviouslySelected = true;
      }
    }
    
    // Eğer önceden hosting seçilmişse, flag'i true yap (fiyatları otomatik getirme)
    // Eğer önceden hosting seçilmemişse, flag'i false yap (yeni hosting seçildiğinde fiyatları getir)
    setHostingPriceAutoFilled(wasHostingPreviouslySelected);
    
    // Tarih formatını düzelt - backend'den YYYY-MM-DD formatında geliyor
    const formatDateForInput = (dateValue) => {
      if (!dateValue) return '';
      
      // Eğer zaten YYYY-MM-DD formatındaysa olduğu gibi döndür
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      
      // String ise sadece tarih kısmını al (ISO formatından)
      if (typeof dateValue === 'string') {
        const dateStr = dateValue.split('T')[0] || dateValue.split(' ')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          return dateStr;
        }
      }
      
      // Date objesi ise (eski veriler için) UTC kullanarak parse et
      if (dateValue instanceof Date) {
        const year = dateValue.getUTCFullYear();
        const month = String(dateValue.getUTCMonth() + 1).padStart(2, '0');
        const day = String(dateValue.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      
      return '';
    };
    
    const newFormData = {
      company_id: payment.company_id || '',
      package_name: payment.package_name || '',
      package_type: payment.package_type || 'domain',
      domain_id: payment.domain_id || '',
      selected_hosting_id: selectedHostingId, // Eğer önceden hosting seçilmişse, onu seçili göster
      purchase_price: purchasePrice,
      sale_price: salePrice,
      purchase_currency: payment.purchase_currency || payment.currency || 'TRY',
      sale_currency: payment.sale_currency || payment.currency || 'TRY',
      frequency: payment.frequency || 'monthly',
      start_date: formatDateForInput(payment.start_date),
      next_payment_date: formatDateForInput(payment.next_payment_date),
      payment_probability: payment.payment_probability !== null && payment.payment_probability !== undefined ? payment.payment_probability : 100,
      notes: payment.notes || ''
    };
    
    setFormData(newFormData);
    
    // Domain tipi ise domainleri getir
    if (payment.package_type === 'domain' && payment.company_id) {
      try {
        const domains = await domainsAPI.getAll();
        // UUID string karşılaştırması (parseInt kullanma!)
        const companyDomains = domains.filter(d => String(d.company_id) === String(payment.company_id));
        setCompanyDomains(Array.isArray(companyDomains) ? companyDomains : []);
      } catch (error) {
        console.error('Domain fetch error:', error);
        setCompanyDomains([]);
      }
    } else {
      setCompanyDomains([]);
    }
    
    setShowAddForm(false);
    setShowEditForm(true);
  };

  const handleExtendPeriod = async (payment) => {
    // Modal aç - süreyi uzat ve cari takibe ekleme seçeneklerini göster
    setExtendingPayment(payment);
    
    // Varsayılan tutarı hesapla (satış fiyatı)
    const defaultAmount = payment.sale_price || 0;
    const defaultDescription = `${payment.package_name} - ${payment.frequency === 'monthly' ? '1 aylık' : '1 yıllık'} uzatma`;
    
    setExtendFormData({
      addToAccounting: false,
      accountId: '',
      amount: defaultAmount.toString(),
      description: defaultDescription
    });
    
    setShowExtendModal(true);
  };

  const confirmExtendPeriod = async () => {
    if (!extendingPayment) return;

    try {
      // Extend payment - next_payment_date'i güncelle
      const currentDate = new Date(extendingPayment.next_payment_date || extendingPayment.start_date);
      let newDate;
      if (extendingPayment.frequency === 'monthly') {
        newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + 1);
      } else if (extendingPayment.frequency === 'yearly') {
        newDate = new Date(currentDate);
        newDate.setFullYear(newDate.getFullYear() + 1);
      } else {
        // Varsayılan olarak 1 yıl ekle
        newDate = new Date(currentDate);
        newDate.setFullYear(newDate.getFullYear() + 1);
      }
      
      await companyPaymentsAPI.update(extendingPayment.id, {
        next_payment_date: newDate.toISOString().split('T')[0]
      });
      
      // Cari takibe eklenecekse
      if (extendFormData.addToAccounting && extendFormData.accountId) {
        const selectedAccount = accounts.find(a => a.id === extendFormData.accountId);
        if (selectedAccount) {
          // Hesap para birimine göre tutarı hesapla
          let amountInAccountCurrency = parseFloat(extendFormData.amount) || 0;
          
          // Eğer payment para birimi ile hesap para birimi farklıysa çevir
          if (extendingPayment.sale_currency !== selectedAccount.currency) {
            const paymentCurrencyRate = exchangeRates[extendingPayment.sale_currency] || 1;
            const accountCurrencyRate = exchangeRates[selectedAccount.currency] || 1;
            // Önce TRY'ye çevir, sonra hesap para birimine
            const amountInTRY = amountInAccountCurrency * paymentCurrencyRate;
            amountInAccountCurrency = amountInTRY / accountCurrencyRate;
          }
          
          await accountTransactionsAPI.create({
            account_id: extendFormData.accountId,
            transaction_type: 'expense',
            amount: amountInAccountCurrency,
            currency: selectedAccount.currency,
            description: extendFormData.description,
            transaction_date: new Date().toISOString().split('T')[0]
          });
          
          toast.success(`Süre uzatıldı ve ${selectedAccount.account_name} hesabına ${getCurrencySymbol(selectedAccount.currency)} ${amountInAccountCurrency.toFixed(2)} harcama olarak eklendi`);
        }
      } else {
        const extendedDuration = extendingPayment.frequency === 'monthly' ? t('companyPayments.oneMonth') : t('companyPayments.oneYear');
        toast.success(t('companyPayments.extendedSuccess', { duration: extendedDuration }).replace('{{duration}}', extendedDuration));
      }
      
      setShowExtendModal(false);
      setExtendingPayment(null);
      fetchData();
    } catch (error) {
      console.error('Period extension error:', error);
      toast.error(error.response?.data?.error || t('companyPayments.extendError'));
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm(t('companyPayments.deleteConfirm'))) {
      return;
    }

    try {
      await companyPaymentsAPI.delete(paymentId);
      toast.success(t('companyPayments.paymentDeleted'));
      fetchData();
    } catch (error) {
      console.error('Payment deletion error:', error);
      toast.error(error.response?.data?.error || t('companyPayments.deleteError'));
    }
  };

  const handleToggleStatus = async (paymentId, currentStatus) => {
    try {
      const response = await fetch(`/api/company-payments/${paymentId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || (data.data.is_active ? 'Ödeme aktif edildi' : 'Ödeme pasif edildi'));
        fetchData();
      } else {
        toast.error(data.error || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Toggle status error:', error);
      toast.error('Durum değiştirilemedi');
    }
  };

  const resetForm = () => {
    setFormData({
      company_id: '',
      package_name: '',
      package_type: 'domain',
      domain_id: '',
      selected_hosting_id: '',
      purchase_price: '',
      sale_price: '',
      purchase_currency: 'TRY',
      sale_currency: 'TRY',
      frequency: 'yearly',
      start_date: '',
      next_payment_date: '',
      payment_probability: 100,
      notes: ''
    });
    setCompanyDomains([]);
    setHostingPriceAutoFilled(false);
    setShowAddForm(false);
    setShowEditForm(false);
    setEditingPayment(null);
  };

  const getPackageTypeText = (type) => {
    const types = {
      domain: t('companyPayments.typeDomain'),
      hosting: t('companyPayments.typeHosting'),
      ssl: t('companyPayments.typeSsl'),
      email: t('companyPayments.typeEmail'),
      reklam: t('companyPayments.typeReklam'),
      danismanlik: t('companyPayments.typeDanismanlik'),
      other: t('companyPayments.typeOther')
    };
    return types[type] || type;
  };

  const getFrequencyText = (frequency) => {
    const frequencies = {
      monthly: t('companyPayments.freqMonthly'),
      yearly: t('companyPayments.freqYearly'),
      'one-time': t('companyPayments.freqOneTime')
    };
    return frequencies[frequency] || frequency;
  };

  const getDaysUntilNextPayment = (nextPaymentDate) => {
    if (!nextPaymentDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const next = new Date(nextPaymentDate);
    next.setHours(0, 0, 0, 0);
    const diffTime = next - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleWhoisLookup = async () => {
    console.log('WHOIS Lookup - formData.domain_id:', formData.domain_id);
    console.log('WHOIS Lookup - companyDomains:', companyDomains);
    
    if (!formData.domain_id) {
      toast.error('Lütfen önce bir domain seçin');
      return;
    }

    // UUID string karşılaştırması (parseInt kullanma!)
    const selectedDomain = companyDomains.find(d => String(d.id) === String(formData.domain_id));
    console.log('WHOIS Lookup - selectedDomain:', selectedDomain);
    
    if (!selectedDomain || !selectedDomain.domain_name) {
      toast.error('Domain bilgisi bulunamadı. Lütfen şirketi seçtikten sonra tekrar deneyin.');
      return;
    }

    const domain = selectedDomain.domain_name.trim().toLowerCase();
    const maxRetries = 3;
    let lastError = null;

    setLoadingWhois(true);
    toast.loading(t('companyPayments.whoisLoading'), { id: 'whois' });

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Backend WHOIS proxy endpoint - CORS sorununu çözer
        const apiUrl = `${getApiBaseUrl()}/whois?domain=${encodeURIComponent(domain)}`;
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`API yanıt hatası: ${response.status}`);
        }
        
        const whoisText = await response.text();
        
        // "No match" veya "Connection reset" kontrol et
        if (whoisText.includes('No match found') || whoisText.includes('Connection reset')) {
          if (attempt < maxRetries) {
            console.log(`⏳ WHOIS deneme ${attempt}/${maxRetries} başarısız, yeniden deneniyor...`);
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            continue;
          }
          toast.error(t('companyPayments.whoisParseError'), { id: 'whois', duration: 5000 });
          return;
        }
        
        // WHOIS verilerini parse et
        const parsedData = parseWhoisResponse(whoisText, domain);
        
        if (!parsedData) {
          if (attempt < maxRetries) {
            console.log(`⏳ WHOIS parse başarısız, deneme ${attempt}/${maxRetries}, yeniden deneniyor...`);
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            continue;
          }
          toast.error(t('companyPayments.whoisParseError'), { id: 'whois', duration: 5000 });
          return;
        }
        
        // Null verileri kontrol et - eğer tarih bilgisi yoksa uyarı ver
        if (!parsedData.registration_date && !parsedData.expiry_date) {
          toast.error(t('companyPayments.whoisNoDate'), { id: 'whois', duration: 5000 });
          return;
        }
        
        // Sadece null olmayan tarihleri forma doldur
        const updatedFormData = { ...formData };
        
        // Başlangıç tarihi - registration_date varsa onu kullan, yoksa mevcut değeri koru
        if (parsedData.registration_date) {
          updatedFormData.start_date = parsedData.registration_date;
        }
        
        // Sonraki ödeme tarihi - expiry_date varsa onu kullan, yoksa mevcut değeri koru
        if (parsedData.expiry_date) {
          updatedFormData.next_payment_date = parsedData.expiry_date;
        }
        
        setFormData(updatedFormData);
        toast.success(t('companyPayments.whoisSuccess'), { id: 'whois' });
        return;
        
      } catch (error) {
        lastError = error;
        console.error(`❌ WHOIS deneme ${attempt}/${maxRetries} hatası:`, error.message);
        
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          continue;
        }
      }
    }
    
    // Tüm denemeler başarısız
    console.error('WHOIS query error:', lastError);
    toast.error(t('companyPayments.whoisError', { error: lastError?.message || t('common.unknownError') }).replace('{{error}}', lastError?.message || t('common.unknownError')), {
      id: 'whois',
      duration: 5000
    });
    setLoadingWhois(false);
  };

  // WHOIS yanıtını parse eden yardımcı fonksiyon
  // Softsynex WHOIS API - düz metin WHOIS çıktısı parse eder
  const parseWhoisResponse = (whoisText, domain) => {
    try {
      if (!whoisText || typeof whoisText !== 'string') {
        return null;
      }

      let registrationDate = null;
      let expiryDate = null;
      let registrar = null;
      let ns1 = null;
      let ns2 = null;
      let status = 'active';
      const nameServers = [];
      let inDomainServersSection = false;
      let inRegistrarSection = false;

      // Satır satır parse et
      const lines = whoisText.split('\n');
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine || trimmedLine.startsWith('>>>') || trimmedLine.startsWith('NOTICE:') || trimmedLine.startsWith('TERMS OF USE:')) {
          continue;
        }

        // .tr domainleri için bölüm tespiti
        if (trimmedLine === '** Domain Servers:') {
          inDomainServersSection = true;
          inRegistrarSection = false;
          continue;
        }
        if (trimmedLine === '** Registrar:') {
          inRegistrarSection = true;
          inDomainServersSection = false;
          continue;
        }
        if (trimmedLine.startsWith('** ') && trimmedLine !== '** Domain Servers:' && trimmedLine !== '** Registrar:') {
          inDomainServersSection = false;
          inRegistrarSection = false;
        }

        // .tr domainleri için Domain Servers bölümündeki NS'leri al
        if (inDomainServersSection) {
          // NS satırları genellikle sadece domain ismi içerir
          if (trimmedLine && !trimmedLine.includes(':') && trimmedLine.includes('.')) {
            const nsValue = trimmedLine.toUpperCase();
            if (!nameServers.includes(nsValue)) {
              nameServers.push(nsValue);
            }
          }
          continue;
        }

        // Key: Value formatını parse et
        const colonIndex = trimmedLine.indexOf(':');
        if (colonIndex === -1) continue;
        
        // .tr domainleri için özel key formatı (Created on..............: gibi)
        let key = trimmedLine.substring(0, colonIndex).trim().toLowerCase();
        // Nokta karakterlerini temizle
        key = key.replace(/\.+$/, '').trim();
        
        const value = trimmedLine.substring(colonIndex + 1).trim();
        
        if (!value) continue;

        // Creation Date - farklı varyasyonlar (standart WHOIS + .tr formatı)
        if (key === 'creation date' || key === 'created date' || key === 'created' ||
            key === 'registration date' || key === 'domain registration date' ||
            key === 'created on') {
          registrationDate = value;
        }
        
        // Expiry Date - farklı varyasyonlar (standart WHOIS + .tr formatı)
        if (key === 'registry expiry date' || key === 'registrar registration expiration date' ||
            key === 'expiration date' || key === 'expiry date' || key === 'expires' ||
            key === 'domain expiration date' || key === 'paid-till' ||
            key === 'expires on') {
          expiryDate = value;
        }
        
        // Registrar - standart format
        if (key === 'registrar' && !registrar) {
          registrar = value;
        }
        
        // Registrar - .tr formatı (Organization Name in Registrar section)
        if (inRegistrarSection && key === 'organization name' && !registrar) {
          registrar = value;
        }
        
        // Name Servers - standart format
        if (key === 'name server' || key === 'nserver' || key === 'nameserver') {
          const nsValue = value.split(' ')[0].toUpperCase(); // IP adresini kaldır
          if (!nameServers.includes(nsValue)) {
            nameServers.push(nsValue);
          }
        }
        
        // Status - domain durumu
        if (key === 'domain status' || key === 'status') {
          const statusLower = value.toLowerCase();
          if (statusLower.includes('expired') || statusLower.includes('redemption') ||
              statusLower.includes('pending delete')) {
            status = 'inactive';
          }
        }
      }

      // Name server'ları ata
      if (nameServers.length > 0) {
        ns1 = nameServers[0];
        ns2 = nameServers[1] || null;
      }

      // Eğer hiçbir veri bulunamadıysa null döndür
      if (!registrationDate && !expiryDate && !registrar && !ns1) {
        console.log('⚠️ WHOIS parse: Hiçbir veri bulunamadı');
        return null;
      }
      
      const parsed = {
        registration_date: registrationDate ? formatDate(registrationDate) : null,
        expiry_date: expiryDate ? formatDate(expiryDate) : null,
        status: status || 'active',
        ns1: ns1,
        ns2: ns2,
        registrar: registrar
      };
      
      return parsed;
      
    } catch (error) {
      console.error('WHOIS parse error:', error);
      return null;
    }
  };

  // Tarih formatını düzenle (YYYY-MM-DD)
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      // .tr formatı için: "2020-Nov-02." veya "2020-Nov-02" (YYYY-Mon-DD)
      let processedDate = dateString;
      
      // Eğer tarih "YYYY-Mon-DD" formatındaysa (örn: "2020-Nov-02"), çevir
      const trMatch = processedDate.match(/^(\d{4})-([A-Za-z]{3})-(\d{2})\.?$/);
      if (trMatch) {
        const [, year, monthStr, day] = trMatch;
        const months = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
                        Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12' };
        const month = months[monthStr] || '01';
        return `${year}-${month}-${day}`;
      }
      
      const date = new Date(processedDate);
      if (isNaN(date.getTime())) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return null;
    }
  };

  // Filtrelenmiş faturalar
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <ArrowUp size={14} className="inline ml-1" /> : <ArrowDown size={14} className="inline ml-1" />;
  };

  // XLSX Export fonksiyonu
  const handleExport = () => {
    const dataToExport = filteredPayments.map(payment => ({
      [t('companyPayments.company')]: payment.company_name || '',
      [t('companyPayments.packageName')]: payment.package_name || '',
      [t('companyPayments.packageType')]: payment.package_type === 'domain' ? t('companyPayments.typeDomain') : payment.package_type === 'hosting' ? t('companyPayments.typeHosting') : t('companyPayments.typeOther'),
      [t('companyPayments.domain')]: payment.domain_name || '',
      [t('companyPayments.purchasePrice')]: payment.purchase_price || 0,
      [t('companyPayments.purchaseCurrency')]: payment.purchase_currency || 'TRY',
      [t('companyPayments.salePrice')]: payment.sale_price || 0,
      [t('companyPayments.saleCurrency')]: payment.sale_currency || 'TRY',
      [t('companyPayments.frequency')]: payment.frequency === 'monthly' ? t('companyPayments.freqMonthly') : payment.frequency === 'yearly' ? t('companyPayments.freqYearly') : t('companyPayments.freqOneTime'),
      [t('companyPayments.startDate')]: payment.start_date ? new Date(payment.start_date).toLocaleDateString('tr-TR') : '',
      [t('companyPayments.nextPayment')]: payment.next_payment_date ? new Date(payment.next_payment_date).toLocaleDateString('tr-TR') : '',
      [t('companyPayments.paymentProbability')]: payment.payment_probability || 100,
      [t('common.status')]: payment.is_active ? t('companyPayments.statusActive') : t('companyPayments.statusPassive'),
      [t('companyPayments.notes')]: payment.notes || ''
    }));

    // XLSX oluştur
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('companyPayments.title'));
    
    // Dosyayı indir
    XLSX.writeFile(wb, `recurring_payments_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    toast.success(t('companyPayments.exportSuccess'));
  };

  // Hizmet türü sayılarını hesapla
  const packageTypeCounts = companyPayments.reduce((acc, payment) => {
    const type = payment.package_type || 'other';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  // Hizmet türleri için renk ve ikon
  const packageTypeConfig = {
    domain: { name: t('companyPayments.typeDomain'), icon: Globe, color: 'blue', bgColor: 'bg-blue-50 dark:bg-blue-900/20', borderColor: 'border-blue-200 dark:border-blue-800', textColor: 'text-blue-700 dark:text-blue-300' },
    hosting: { name: t('companyPayments.typeHosting'), icon: Layers, color: 'green', bgColor: 'bg-green-50 dark:bg-green-900/20', borderColor: 'border-green-200 dark:border-green-800', textColor: 'text-green-700 dark:text-green-300' },
    ssl: { name: t('companyPayments.typeSsl'), icon: CreditCard, color: 'yellow', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', borderColor: 'border-yellow-200 dark:border-yellow-800', textColor: 'text-yellow-700 dark:text-yellow-300' },
    email: { name: t('companyPayments.typeEmail'), icon: Mail, color: 'purple', bgColor: 'bg-purple-50 dark:bg-purple-900/20', borderColor: 'border-purple-200 dark:border-purple-800', textColor: 'text-purple-700 dark:text-purple-300' },
    reklam: { name: t('companyPayments.typeReklam'), icon: DollarSign, color: 'pink', bgColor: 'bg-pink-50 dark:bg-pink-900/20', borderColor: 'border-pink-200 dark:border-pink-800', textColor: 'text-pink-700 dark:text-pink-300' },
    danismanlik: { name: t('companyPayments.typeDanismanlik'), icon: Building2, color: 'indigo', bgColor: 'bg-indigo-50 dark:bg-indigo-900/20', borderColor: 'border-indigo-200 dark:border-indigo-800', textColor: 'text-indigo-700 dark:text-indigo-300' },
    other: { name: t('companyPayments.typeOther'), icon: Package, color: 'gray', bgColor: 'bg-gray-50 dark:bg-gray-900/20', borderColor: 'border-gray-200 dark:border-gray-800', textColor: 'text-gray-700 dark:text-gray-300' }
  };

  // UUID string karşılaştırması (parseInt kullanma!)
  const filteredPayments = (filterCompanyId
    ? companyPayments.filter(payment => String(payment.company_id) === String(filterCompanyId))
    : companyPayments
  ).filter(payment => {
    // Status filter
    if (statusFilter === 'active' && !payment.is_active) return false;
    if (statusFilter === 'inactive' && payment.is_active) return false;
    // Package type filter
    if (packageTypeFilter && payment.package_type !== packageTypeFilter) return false;
    return true;
  }).sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aValue, bValue;
    
    switch (sortColumn) {
      case 'company_name':
        aValue = a.company_name?.toLowerCase() || '';
        bValue = b.company_name?.toLowerCase() || '';
        break;
      case 'package_name':
        aValue = a.package_name?.toLowerCase() || '';
        bValue = b.package_name?.toLowerCase() || '';
        break;
      case 'package_type':
        aValue = a.package_type?.toLowerCase() || '';
        bValue = b.package_type?.toLowerCase() || '';
        break;
      case 'purchase_price':
        aValue = parseFloat(a.purchase_price) || 0;
        bValue = parseFloat(b.purchase_price) || 0;
        break;
      case 'sale_price':
        aValue = parseFloat(a.sale_price) || 0;
        bValue = parseFloat(b.sale_price) || 0;
        break;
      case 'frequency':
        aValue = a.frequency?.toLowerCase() || '';
        bValue = b.frequency?.toLowerCase() || '';
        break;
      case 'next_payment_date':
        aValue = a.next_payment_date ? new Date(a.next_payment_date).getTime() : 0;
        bValue = b.next_payment_date ? new Date(b.next_payment_date).getTime() : 0;
        break;
      case 'payment_probability':
        aValue = parseFloat(a.payment_probability) || 0;
        bValue = parseFloat(b.payment_probability) || 0;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Firmalara göre gruplandırılmış faturalar
  const groupedPayments = groupByCompany 
    ? filteredPayments.reduce((acc, payment) => {
        const companyId = payment.company_id;
        if (!acc[companyId]) {
          acc[companyId] = {
            company_id: companyId,
            company_name: payment.company_name || t('companyPayments.unknownCompany'),
            payments: [],
            totals: {
              totalPurchase: 0,
              totalSale: 0,
              totalProfit: 0
            }
          };
        }
        acc[companyId].payments.push(payment);
        
        // Toplamları hesapla
        const purchasePrice = parseFloat(payment.purchase_price) || 0;
        const salePrice = parseFloat(payment.sale_price) || 0;
        const purchaseCurrency = payment.purchase_currency || payment.currency || 'TRY';
        const saleCurrency = payment.sale_currency || payment.currency || 'TRY';
        
        // Alış fiyatını TL'ye çevir
        let purchaseInTRY = purchasePrice;
        if (purchaseCurrency !== 'TRY') {
          const rate = exchangeRates[purchaseCurrency] || 1;
          purchaseInTRY = purchasePrice * rate;
        }
        
        // Satış fiyatını TL'ye çevir
        let saleInTRY = salePrice;
        if (saleCurrency !== 'TRY') {
          const rate = exchangeRates[saleCurrency] || 1;
          saleInTRY = salePrice * rate;
        }
        
        acc[companyId].totals.totalPurchase += purchaseInTRY;
        acc[companyId].totals.totalSale += saleInTRY;
        acc[companyId].totals.totalProfit += (saleInTRY - purchaseInTRY);
        
        return acc;
      }, {})
    : null;

  // Domain fiyatını getir
  // Domain uzantısını çıkar (çoklu uzantıları destekler: .com.tr, .av.tr, vb.)
  const extractDomainExtension = (domainName) => {
    if (!domainName) return '';
    
    const domain = domainName.toLowerCase().trim();
    
    // Bilinen çoklu uzantılar (önce bunları kontrol et)
    const multiExtensions = [
      '.com.tr', '.net.tr', '.org.tr', '.edu.tr', '.gov.tr',
      '.av.tr', '.bbs.tr', '.bel.tr', '.biz.tr', '.gen.tr',
      '.info.tr', '.k12.tr', '.mil.tr', '.name.tr', '.tel.tr',
      '.tv.tr', '.web.tr', '.co.uk', '.com.au', '.co.nz',
      '.com.br', '.com.mx', '.co.za', '.com.ar', '.com.co'
    ];
    
    // Önce çoklu uzantıları kontrol et
    for (const ext of multiExtensions) {
      if (domain.endsWith(ext)) {
        return ext;
      }
    }
    
    // Çoklu uzantı bulunamazsa, en son noktadan sonrasını al (.com, .net, vb.)
    const lastDotIndex = domain.lastIndexOf('.');
    if (lastDotIndex !== -1) {
      return domain.substring(lastDotIndex);
    }
    
    return '';
  };

  const handleGetDomainPrice = async () => {
    if (!formData.domain_id) {
      toast.error(t('companyPayments.selectDomainFirst'));
      return;
    }

    // UUID string karşılaştırması (parseInt kullanma!)
    const selectedDomain = companyDomains.find(d => String(d.id) === String(formData.domain_id));
    if (!selectedDomain || !selectedDomain.domain_name) {
      toast.error(t('companyPayments.domainInfoNotFound'));
      return;
    }

    try {
      setLoadingDomainPrice(true);
      // Domain uzantısını çıkar (.com, .com.tr, .av.tr, vb.)
      const domainName = selectedDomain.domain_name.toLowerCase();
      const extension = extractDomainExtension(domainName);
      
      if (!extension) {
        toast.error(t('companyPayments.domainExtensionError'));
        setLoadingDomainPrice(false);
        return;
      }
      
      
      // Supabase'den domain fiyatını getir
      const priceData = await domainPricesAPI.getByExtension(extension);
      
      if (!priceData) {
        toast.error(t('companyPayments.domainPriceNotFound', { extension }).replace('{{extension}}', extension));
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        purchase_price: priceData.purchase_price !== null && priceData.purchase_price !== undefined ? String(priceData.purchase_price) : '',
        sale_price: priceData.sale_price !== null && priceData.sale_price !== undefined ? String(priceData.sale_price) : '',
        purchase_currency: priceData.purchase_currency || 'TRY',
        sale_currency: priceData.sale_currency || 'TRY'
      }));

      toast.success(t('companyPayments.domainPriceSuccess'));
    } catch (error) {
      console.error('Domain price fetch error:', error);
      // UUID string karşılaştırması (parseInt kullanma!)
      const domainName = companyDomains.find(d => String(d.id) === String(formData.domain_id))?.domain_name?.toLowerCase() || '';
      const extension = extractDomainExtension(domainName);
      if (error.code === 'PGRST116') {
        toast.error(t('companyPayments.domainPriceNotFound', { extension }).replace('{{extension}}', extension));
      } else {
        toast.error(error.message || t('companyPayments.domainPriceError'));
      }
    } finally {
      setLoadingDomainPrice(false);
    }
  };

  // Hosting fiyatını getir
  const handleGetHostingPrice = async () => {
    if (!formData.package_name) {
      toast.error(t('companyPayments.enterPackageName'));
      return;
    }

    let hostingNameToSearch = formData.package_name;
    
    try {
      setLoadingHostingPrice(true);
      
      // Eğer hosting seçildiyse, hosting adını kullan
      if (formData.selected_hosting_id) {
        const selectedHosting = hostingPackages.find(h => h.id === parseInt(formData.selected_hosting_id));
        if (selectedHosting) {
          hostingNameToSearch = selectedHosting.name;
        }
      } else {
        // Eğer hosting seçilmemişse, package_name'den sadece sunucu adını çıkar
        // Format: "Sunucu Adı (Provider) - Plan" -> "Sunucu Adı"
        const match = formData.package_name.match(/^([^(]+)/);
        if (match) {
          hostingNameToSearch = match[1].trim();
        }
      }
      
      
      // Supabase'den hosting fiyatını getir
      const priceData = await hostingPricesAPI.getByName(hostingNameToSearch);
      
      if (!priceData) {
        toast.error(
          t('companyPayments.hostingPriceNotFound', { name: hostingNameToSearch }).replace('{{name}}', hostingNameToSearch),
          { duration: 5000 }
        );
        return;
      }
      
      // Fiyatları güncelle, ama purchase_price 0 olsa bile sale_price'ı koru (eğer manuel girilmişse)
      setFormData(prev => {
        // Eğer kullanıcı manuel olarak sale_price girmişse ve purchase_price 0 ise, sale_price'ı koru
        const shouldPreserveSalePrice = parseFloat(prev.sale_price) > 0 && parseFloat(priceData.purchase_price) === 0;
        
        return {
          ...prev,
          purchase_price: priceData.purchase_price !== null && priceData.purchase_price !== undefined ? String(priceData.purchase_price) : '',
          sale_price: shouldPreserveSalePrice ? prev.sale_price : (priceData.sale_price !== null && priceData.sale_price !== undefined ? String(priceData.sale_price) : ''),
          purchase_currency: priceData.purchase_currency || 'TRY',
          sale_currency: priceData.sale_currency || 'TRY'
        };
      });

      toast.success(t('companyPayments.hostingPriceSuccess'));
    } catch (error) {
      console.error('Hosting price fetch error:', error);
      if (error.code === 'PGRST116') {
        toast.error(
          t('companyPayments.hostingPriceNotFound', { name: hostingNameToSearch }).replace('{{name}}', hostingNameToSearch),
          { duration: 5000 }
        );
      } else {
        toast.error(error.message || t('companyPayments.hostingPriceError'));
      }
    } finally {
      setLoadingHostingPrice(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-10 p-8">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Siparişler</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-lg">{t('companyPayments.subtitle', 'Tüm faturaları yönetin')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={handleExport}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Download className="h-5 w-5" />
              <span className="font-medium">{t('companyPayments.exportToExcel', 'Export')}</span>
            </button>
            <button 
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Plus className="h-5 w-5" />
              <span className="font-medium">{t('companyPayments.newPayment', 'Yeni Ödeme')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bilgi Notu */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-700 p-4 rounded-lg">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
              {t('common.connectorInfo')}
            </p>
          </div>
        </div>
      </div>

      {/* Fatura Ekleme Formu Modal */}
      <PaymentModal
        isOpen={showAddForm}
        onClose={resetForm}
        title={t('common.newRecurringPayment')}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.company')} *
                </label>
                <select
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('common.selectCompany')}</option>
                  {[...companies].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')).map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.packageType')} *
                </label>
                <select
                  name="package_type"
                  value={formData.package_type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="domain">{t('companyPayments.typeDomain')}</option>
                  <option value="hosting">{t('companyPayments.typeHosting')}</option>
                  <option value="ssl">{t('companyPayments.typeSsl')}</option>
                  <option value="email">{t('companyPayments.typeEmail')}</option>
                  <option value="reklam">{t('companyPayments.typeReklam')}</option>
                  <option value="danismanlik">{t('companyPayments.typeDanismanlik')}</option>
                  <option value="other">{t('companyPayments.typeOther')}</option>
                </select>
              </div>
              
              {formData.package_type === 'hosting' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('common.selectHostingPackage')}
                  </label>
                  <select
                    name="selected_hosting_id"
                    value={formData.selected_hosting_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">{t('common.manualEntry')}</option>
                    {hostingPackages.length > 0 ? (
                      hostingPackages.map(hosting => (
                        <option key={hosting.id} value={hosting.id}>
                          {hosting.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>{t('common.noHostingPrice')}</option>
                    )}
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.packageName')} *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    name="package_name"
                    value={formData.package_name}
                    onChange={handleInputChange}
                    required
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('common.examplePackage')}
                  />
                  {formData.package_type === 'hosting' && formData.package_name && (
                    <button
                      type="button"
                      onClick={handleGetHostingPrice}
                      disabled={loadingHostingPrice}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={t('common.getDomainPrice')}
                    >
                      {loadingHostingPrice ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <DollarSign className="h-4 w-4" />
                      )}
                      <span className="hidden sm:inline">{t('common.price')}</span>
                    </button>
                  )}
                </div>
              </div>
              
              {formData.package_type === 'domain' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('common.selectDomain')}
                  </label>
                  <div className="flex space-x-2">
                    <select
                      name="domain_id"
                      value={formData.domain_id}
                      onChange={handleInputChange}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">{t('common.selectDomainOptional')}</option>
                      {companyDomains.map(domain => (
                        <option key={domain.id} value={domain.id}>
                          {domain.domain_name} {domain.customer_name ? `(${domain.customer_name})` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={handleWhoisLookup}
                        disabled={loadingWhois}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t('common.getDatesFromWhois')}
                      >
                        {loadingWhois ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Globe className="h-4 w-4" />
                        )}
                        <span className="hidden sm:inline">{t('common.whois')}</span>
                      </button>
                        <button
                          type="button"
                          onClick={handleGetDomainPrice}
                          disabled={loadingDomainPrice}
                          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t('common.getDomainPrice')}
                        >
                          {loadingDomainPrice ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <DollarSign className="h-4 w-4" />
                          )}
                          <span className="hidden sm:inline">{t('common.price')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                  {companyDomains.length === 0 && formData.company_id && (
                    <p className="text-xs text-gray-500 mt-1">{t('common.noDomainFound')}</p>
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.purchasePrice')} *
                </label>
                <div className="flex">
                  <select
                    name="purchase_currency"
                    value={formData.purchase_currency}
                    onChange={handleInputChange}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="TRY">₺</option>
                    <option value="USD">$</option>
                    <option value="EUR">€</option>
                  </select>
                  <input
                    type="number"
                    name="purchase_price"
                    value={formData.purchase_price}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.salePrice')} *
                </label>
                <div className="flex">
                  <select
                    name="sale_currency"
                    value={formData.sale_currency}
                    onChange={handleInputChange}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="TRY">₺</option>
                    <option value="USD">$</option>
                    <option value="EUR">€</option>
                  </select>
                  <input
                    type="number"
                    name="sale_price"
                    value={formData.sale_price}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.frequency')} *
                </label>
                <select
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="monthly">{t('common.monthly')}</option>
                  <option value="yearly">{t('common.yearly')}</option>
                  <option value="one-time">{t('common.oneTime')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.paymentProbability')} (%)
                </label>
                <input
                  type="number"
                  name="payment_probability"
                  value={formData.payment_probability}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="100"
                />
                <p className="text-xs text-gray-500 mt-1">{t('common.paymentProbabilityHint')}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.startDate')}
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.nextPayment')}
                </label>
                <input
                  type="date"
                  name="next_payment_date"
                  value={formData.next_payment_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.notes')}
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('common.additionalNotes')}
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
              >
                {t('common.addPayment')}
              </button>
            </div>
          </form>
        </PaymentModal>

      {/* Fatura Düzenleme Formu Modal */}
      <PaymentModal
        isOpen={showEditForm}
        onClose={resetForm}
        title={t('common.editRecurringPayment')}
      >
        {editingPayment && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.company')} *
                </label>
                <select
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('common.selectCompany')}</option>
                  {[...companies].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')).map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.packageName')} *
                </label>
                <input
                  type="text"
                  name="package_name"
                  value={formData.package_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.packageType')} *
                </label>
                <select
                  name="package_type"
                  value={formData.package_type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="domain">{t('companyPayments.typeDomain')}</option>
                  <option value="hosting">{t('companyPayments.typeHosting')}</option>
                  <option value="ssl">{t('companyPayments.typeSsl')}</option>
                  <option value="email">{t('companyPayments.typeEmail')}</option>
                  <option value="reklam">{t('companyPayments.typeReklam')}</option>
                  <option value="danismanlik">{t('companyPayments.typeDanismanlik')}</option>
                  <option value="other">{t('companyPayments.typeOther')}</option>
                </select>
              </div>
              
              {formData.package_type === 'hosting' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('common.selectHostingPackage')}
                  </label>
                  <select
                    name="selected_hosting_id"
                    value={formData.selected_hosting_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">{t('common.manualEntry')}</option>
                    {hostingPackages.length > 0 ? (
                      hostingPackages.map(hosting => (
                        <option key={hosting.id} value={hosting.id}>
                          {hosting.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>{t('common.noHostingPrice')}</option>
                    )}
                  </select>
                </div>
              )}
              
              {formData.package_type === 'domain' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('common.selectDomain')}
                  </label>
                  <div className="flex space-x-2">
                    <select
                      name="domain_id"
                      value={formData.domain_id}
                      onChange={handleInputChange}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">{t('common.selectDomainOptional')}</option>
                      {companyDomains.map(domain => (
                        <option key={domain.id} value={domain.id}>
                          {domain.domain_name} {domain.customer_name ? `(${domain.customer_name})` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={handleWhoisLookup}
                        disabled={loadingWhois}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t('common.getDatesFromWhois')}
                      >
                        {loadingWhois ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Globe className="h-4 w-4" />
                        )}
                        <span className="hidden sm:inline">{t('common.whois')}</span>
                      </button>
                        <button
                          type="button"
                          onClick={handleGetDomainPrice}
                          disabled={loadingDomainPrice}
                          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t('common.getDomainPrice')}
                        >
                          {loadingDomainPrice ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <DollarSign className="h-4 w-4" />
                          )}
                          <span className="hidden sm:inline">{t('common.price')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                  {companyDomains.length === 0 && formData.company_id && (
                    <p className="text-xs text-gray-500 mt-1">{t('common.noDomainFound')}</p>
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.purchasePrice')} *
                </label>
                <div className="flex">
                  <select
                    name="purchase_currency"
                    value={formData.purchase_currency}
                    onChange={handleInputChange}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="TRY">₺</option>
                    <option value="USD">$</option>
                    <option value="EUR">€</option>
                  </select>
                  <input
                    type="number"
                    name="purchase_price"
                    value={formData.purchase_price}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    required
                    step="0.01"
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.salePrice')} *
                </label>
                <div className="flex">
                  <select
                    name="sale_currency"
                    value={formData.sale_currency}
                    onChange={handleInputChange}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="TRY">₺</option>
                    <option value="USD">$</option>
                    <option value="EUR">€</option>
                  </select>
                  <input
                    type="number"
                    name="sale_price"
                    value={formData.sale_price}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.frequency')} *
                </label>
                <select
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="monthly">{t('common.monthly')}</option>
                  <option value="yearly">{t('common.yearly')}</option>
                  <option value="one-time">{t('common.oneTime')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.paymentProbability')} (%)
                </label>
                <input
                  type="number"
                  name="payment_probability"
                  value={formData.payment_probability}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="100"
                />
                <p className="text-xs text-gray-500 mt-1">{t('common.paymentProbabilityHint')}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.startDate')}
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.nextPayment')}
                </label>
                <input
                  type="date"
                  name="next_payment_date"
                  value={formData.next_payment_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('common.notes')}
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
              >
                {t('common.update')}
              </button>
            </div>
          </form>
        )}
      </PaymentModal>

      {/* Süre Uzatma Modalı */}
      <PaymentModal
        isOpen={showExtendModal}
        onClose={() => setShowExtendModal(false)}
        title="Süreyi Uzat"
      >
        {extendingPayment && (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  {extendingPayment.package_name}
                </h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {extendingPayment.frequency === 'monthly' ? '1 aylık' : '1 yıllık'} uzatma yapılacak
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Mevcut son ödeme tarihi: {extendingPayment.next_payment_date ? new Date(extendingPayment.next_payment_date).toLocaleDateString('tr-TR') : '-'}
              </p>
            </div>

            {/* Cari Takibe Ekleme Seçeneği */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={extendFormData.addToAccounting}
                  onChange={(e) => setExtendFormData({ ...extendFormData, addToAccounting: e.target.checked })}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex items-center space-x-2">
                  <Wallet className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Cari takibe ekle
                  </span>
                </div>
              </label>
            </div>

            {extendFormData.addToAccounting && (
              <div className="space-y-4 pl-8 border-l-2 border-blue-200 dark:border-blue-800">
                {/* Hesap Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Hesap Seçin
                  </label>
                  <select
                    value={extendFormData.accountId}
                    onChange={(e) => setExtendFormData({ ...extendFormData, accountId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  >
                    <option value="">Hesap seçin...</option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.account_name} ({getCurrencySymbol(account.currency)})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Tutar */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tutar ({getCurrencySymbol(extendingPayment.sale_currency)})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={extendFormData.amount}
                    onChange={(e) => setExtendFormData({ ...extendFormData, amount: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>

                {/* Açıklama */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Açıklama
                  </label>
                  <input
                    type="text"
                    value={extendFormData.description}
                    onChange={(e) => setExtendFormData({ ...extendFormData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Açıklama..."
                  />
                </div>

                {/* Kur Bilgisi */}
                {extendFormData.accountId && (() => {
                  const selectedAccount = accounts.find(a => a.id === extendFormData.accountId);
                  if (selectedAccount && extendingPayment.sale_currency !== selectedAccount.currency) {
                    const paymentRate = exchangeRates[extendingPayment.sale_currency] || 1;
                    const accountRate = exchangeRates[selectedAccount.currency] || 1;
                    const amountInTRY = (parseFloat(extendFormData.amount) || 0) * paymentRate;
                    const convertedAmount = amountInTRY / accountRate;
                    return (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Kur Çevirme:</span> {extendingPayment.sale_currency} → {selectedAccount.currency}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {getCurrencySymbol(extendingPayment.sale_currency)} {extendFormData.amount} × {paymentRate} / {accountRate} = {getCurrencySymbol(selectedAccount.currency)} {convertedAmount.toFixed(2)}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {/* Butonlar */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowExtendModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                İptal
              </button>
              <button
                onClick={confirmExtendPeriod}
                disabled={extendFormData.addToAccounting && !extendFormData.accountId}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Uzat
              </button>
            </div>
          </div>
        )}
      </PaymentModal>

      {/* Hizmet Türü Filtre Kutuları */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-3">
          {Object.entries(packageTypeConfig).map(([type, config]) => {
            const Icon = config.icon;
            const count = packageTypeCounts[type] || 0;
            const isSelected = packageTypeFilter === type;
            
            return (
              <button
                key={type}
                onClick={() => setPackageTypeFilter(isSelected ? '' : type)}
                className={`
                  relative overflow-hidden rounded-xl border-2 transition-all duration-200 hover:shadow-lg
                  ${isSelected
                    ? `${config.bgColor} ${config.borderColor} ${config.textColor} border-current scale-105 shadow-md`
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
                style={{ minWidth: '140px', maxWidth: '180px' }}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`p-2 rounded-lg ${isSelected ? config.bgColor : 'bg-gray-100 dark:bg-gray-700'}`}>
                      <Icon className={`h-5 w-5 ${isSelected ? config.textColor : 'text-gray-500 dark:text-gray-400'}`} />
                    </div>
                    {isSelected && (
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 rounded-full bg-current animate-pulse"></div>
                      </div>
                    )}
                  </div>
                  <div className={`text-sm font-semibold mb-1 ${isSelected ? config.textColor : 'text-gray-700 dark:text-gray-300'}`}>
                    {config.name}
                  </div>
                  <div className={`text-2xl font-bold ${isSelected ? config.textColor : 'text-gray-900 dark:text-white'}`}>
                    {count}
                  </div>
                  <div className={`text-xs mt-1 ${isSelected ? 'opacity-75' : 'text-gray-500 dark:text-gray-400'}`}>
                    {count === 1 ? 'sipariş' : 'sipariş'}
                  </div>
                </div>
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <X className={`h-4 w-4 ${config.textColor}`} />
                  </div>
                )}
              </button>
            );
          })}
          
          {/* Tümü butonu */}
          {packageTypeFilter && (
            <button
              onClick={() => setPackageTypeFilter('')}
              className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 flex items-center space-x-2 h-fit self-center"
            >
              <X className="h-4 w-4" />
              <span>Filtreyi Temizle</span>
            </button>
          )}
        </div>
      </div>

      {/* Faturalar Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Siparişler</h3>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <select
                  value={filterCompanyId}
                  onChange={(e) => setFilterCompanyId(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('companyPayments.allCompanies', 'Tüm Firmalar')}</option>
                  {[...companies].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')).map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">{t('common.all', 'Tümü')}</option>
                  <option value="active">{t('common.active', 'Aktif')}</option>
                  <option value="inactive">{t('common.inactive', 'Pasif')}</option>
                </select>
              </div>
              {(filterCompanyId || statusFilter !== 'all' || packageTypeFilter) && (
                <button
                  onClick={() => {
                    setFilterCompanyId('');
                    setStatusFilter('all');
                    setPackageTypeFilter('');
                  }}
                  className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                >
                  {t('common.clear', 'Temizle')}
                </button>
              )}
              <button
                onClick={() => setGroupByCompany(!groupByCompany)}
                className={`px-4 py-2 text-sm rounded-lg transition-colors duration-200 flex items-center space-x-2 ${
                  groupByCompany
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Layers className="h-4 w-4" />
                <span>{t('common.groupByCompany')}</span>
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {groupByCompany && groupedPayments ? (
            // Gruplandırılmış görünüm
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {Object.values(groupedPayments).map((group) => (
                <div key={group.company_id} className="p-6">
                  <div className="mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center mb-3">
                      <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{group.company_name}</h4>
                      <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">({group.payments.length} {t('common.payments')})</span>
                    </div>
                    {/* Toplam İstatistikler */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('common.totalPurchase')}</div>
                        <div className="text-lg font-semibold text-gray-700 dark:text-white">
                          ₺ {group.totals.totalPurchase.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('common.totalSale')}</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          ₺ {group.totals.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('common.totalProfitLoss')}</div>
                        <div className={`text-lg font-bold ${group.totals.totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          ₺ {group.totals.totalProfit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {t('common.productService', 'Ürün / Hizmet')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('companyPayments.purchasePrice', 'Alış')} / {t('companyPayments.salePrice', 'Satış')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('companyPayments.nextPayment', 'Sonraki Ödeme')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('common.status', 'Durum')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('common.actions', 'İşlemler')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {group.payments.map((payment) => {
                        const daysUntil = getDaysUntilNextPayment(payment.next_payment_date);
                        const isDueSoon = daysUntil !== null && daysUntil <= 30 && daysUntil >= 0;
                        
                        return (
                          <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{payment.package_name}</div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                    {getPackageTypeText(payment.package_type)}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {getFrequencyText(payment.frequency)}
                                  </span>
                                </div>
                                {payment.domain_name && (
                                  <div className="mt-2">
                                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                      🌐 {payment.domain_name}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-3 text-sm">
                                <div className="flex items-center text-gray-600 dark:text-gray-300">
                                  <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">{t('common.purchase')}:</span>
                                  <span>
                                    {getCurrencySymbol(payment.purchase_currency || payment.currency || 'TRY')} {parseFloat(payment.purchase_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                                <div className="flex items-center font-medium text-gray-900 dark:text-white">
                                  <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">{t('common.sale')}:</span>
                                  <span>
                                    {getCurrencySymbol(payment.sale_currency || payment.currency || 'TRY')} {parseFloat(payment.sale_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                                <div className="flex items-center text-xs font-semibold">
                                  <span className="text-gray-500 dark:text-gray-400 mr-1">{t('common.profit')}:</span>
                                  <span className={`${calculateProfit(payment.purchase_price, payment.purchase_currency || payment.currency || 'TRY', payment.sale_price, payment.sale_currency || payment.currency || 'TRY') >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                    ₺ {calculateProfit(payment.purchase_price, payment.purchase_currency || payment.currency || 'TRY', payment.sale_price, payment.sale_currency || payment.currency || 'TRY').toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap ${isDueSoon ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                              {payment.next_payment_date ? (
                                <div className="flex flex-col">
                                  <div className="flex items-center text-sm">
                                    <Calendar className="h-4 w-4 mr-2" />
                                    {new Date(payment.next_payment_date).toLocaleDateString('tr-TR')}
                                  </div>
                                  {daysUntil !== null && (
                                    <div className={`text-xs mt-1 ${isDueSoon ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                      {daysUntil > 0 ? `${daysUntil} ${t('common.daysLeft', 'gün kaldı')}` : daysUntil === 0 ? t('calendar.today', 'Bugün') : `${Math.abs(daysUntil)} ${t('common.daysPast', 'gün geçti')}`}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">{t('common.na')}</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                payment.is_active
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                  : 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300'
                              }`}>
                                {payment.is_active ? t('common.active') : t('common.passive')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-3">
                                {(payment.frequency === 'monthly' || payment.frequency === 'yearly') && (
                                  <button
                                    onClick={() => handleExtendPeriod(payment)}
                                    className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 transition-colors duration-200"
                                    title={t('common.extendPeriod')}
                                  >
                                    <Clock className="h-4 w-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleToggleStatus(payment.id, payment.is_active)}
                                  className={`${payment.is_active ? 'text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300' : 'text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300'} transition-colors duration-200`}
                                  title={payment.is_active ? 'Pasife al' : 'Aktife al'}
                                >
                                  <PowerOff className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleEdit(payment)}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 transition-colors duration-200"
                                  title={t('common.edit')}
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(payment.id)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-200"
                                  title={t('common.delete')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ) : (
            // Normal tablo görünümü
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                    onClick={() => handleSort('company_name')}
                  >
                    {t('common.company')} {getSortIcon('company_name')}
                  </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('package_name')}
                >
                  {t('common.productService', 'Ürün / Hizmet')} {getSortIcon('package_name')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('purchase_price')}
                >
                  {t('common.purchase')} / {t('common.sale')} {getSortIcon('purchase_price')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('next_payment_date')}
                >
                  {t('common.nextPaymentDate')} {getSortIcon('next_payment_date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('common.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
                </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredPayments.map((payment) => {
                const daysUntil = getDaysUntilNextPayment(payment.next_payment_date);
                const isDueSoon = daysUntil !== null && daysUntil <= 30 && daysUntil >= 0;
                
                return (
                  <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {payment.company_name || t('common.na')}
                          </div>
                          {payment.domain_name && (
                            <div className="mt-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                🌐 {payment.domain_name}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{payment.package_name}</div>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                            {getPackageTypeText(payment.package_type)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {getFrequencyText(payment.frequency)}
                          </span>
                        </div>
                      </div>
                    </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3 text-sm">
                              <div className="flex items-center text-gray-600 dark:text-gray-300">
                                <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">{t('common.purchase')}:</span>
                                <span>
                                  {getCurrencySymbol(payment.purchase_currency || payment.currency || 'TRY')} {parseFloat(payment.purchase_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                              <div className="flex items-center font-medium text-gray-900 dark:text-white">
                                <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">{t('common.sale')}:</span>
                                <span>
                                  {getCurrencySymbol(payment.sale_currency || payment.currency || 'TRY')} {parseFloat(payment.sale_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                              <div className="flex items-center text-xs font-semibold">
                                <span className="text-gray-500 dark:text-gray-400 mr-1">{t('common.profit')}:</span>
                                <span className={`${calculateProfit(payment.purchase_price, payment.purchase_currency || payment.currency || 'TRY', payment.sale_price, payment.sale_currency || payment.currency || 'TRY') >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                  ₺ {calculateProfit(payment.purchase_price, payment.purchase_currency || payment.currency || 'TRY', payment.sale_price, payment.sale_currency || payment.currency || 'TRY').toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            </div>
                          </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isDueSoon ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                      {payment.next_payment_date ? (
                        <div className="flex flex-col">
                          <div className="flex items-center text-sm">
                            <Calendar className="h-4 w-4 mr-2" />
                            {new Date(payment.next_payment_date).toLocaleDateString('tr-TR')}
                          </div>
                          {daysUntil !== null && (
                            <div className={`text-xs mt-1 ${isDueSoon ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                              {daysUntil < 0
                                ? `${t('common.delayed')} (${Math.abs(daysUntil)} ${t('common.days')})`
                                : daysUntil === 0
                                ? t('calendar.today')
                                : `${daysUntil} ${t('common.daysLeft')}`
                              }
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        payment.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300'
                      }`}>
                        {payment.is_active ? t('common.active') : t('common.passive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {(payment.frequency === 'monthly' || payment.frequency === 'yearly') && (
                          <button
                            onClick={() => handleExtendPeriod(payment)}
                            className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 transition-colors duration-200"
                            title={t('common.extendPeriod')}
                          >
                            <Clock className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleToggleStatus(payment.id, payment.is_active)}
                          className={`${payment.is_active ? 'text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300' : 'text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300'} transition-colors duration-200`}
                          title={payment.is_active ? 'Pasife al' : 'Aktife al'}
                        >
                          <PowerOff className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(payment)}
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors duration-200"
                          title={t('common.edit')}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(payment.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-200"
                          title={t('common.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      </div>

      {filteredPayments.length === 0 && companyPayments.length > 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <Search className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{t('common.noResultsFound', 'Filtreye uygun ödeme bulunamadı')}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('common.tryDifferentFilter', 'Farklı bir firma seçin veya filtreyi temizleyin.')}</p>
        </div>
      )}

      {companyPayments.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <CreditCard className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{t('companyPayments.noPayments', 'Henüz tekrarlı ödeme yok')}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('common.addFirstItem', 'İlk tekrarlı ödemeyi ekleyerek başlayın.')}</p>
        </div>
      )}
    </div>
  );
};

export default CompanyPayments;


